import { Stack, styled, Typography } from '@mui/material';

import {
  Link,
  Button,
  Section,
} from '@rumblefish/ui/src/components/Rumblefish23Theme';

import radialGradient from '../../../public/webp/fromPNG/bg-hero2x.webp';
import chequered from '../../../public/webp/fromPNG/chequered.webp';
import keepScrolling from '../../../public/webp/fromPNG/keep-scrolling-circle.webp';
import rumbleStamp from '../../../public/webp/fromPNG/rumble-stamp-orange-white.webp';
import underlineStroke from '../../../public/webp/fromPNG/underline-stroke.webp';

export const StyledHeroSection = styled(Section)({
  height: '100%',
});

export const StyledStack = styled(Stack)(({ theme }) => ({
  position: 'relative',
  flexFlow: 'row nowrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: theme.spacing(12, 0),
  [theme.breakpoints.down('lg')]: {
    margin: theme.spacing(10, 0),
  },
  [theme.breakpoints.down('md')]: {
    margin: theme.spacing(8, 0),
  },
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(6, 0),
  },
}));

export const StyledBackground = styled('div')(({ theme }) => ({
  ['&:before']: {
    content: "''",
    height: `113%`,
    width: `100vw`,
    left: '0%',
    top: '0%',
    margin: `0 calc((-1) * ((100vw - 100%) / 2))`,
    position: 'absolute',
    opacity: 0.4,
    willChange: 'opacity',
    backgroundImage: `url(${radialGradient}), url(${chequered})`,
    backgroundSize: `100% 100%, 100%, 100%`,
    backgroundRepeat: `no-repeat`,
    backgroundPositionX: `10%, 100%, 70%`,
    backgroundPositionY: `10%, 2%`,
    [theme.breakpoints.down('md')]: {
      backgroundSize: `120% 100%, 100%`,
      backgroundPositionX: `50%, 0%, 0%`,
    },
    [theme.breakpoints.down('sm')]: {
      backgroundSize: `130% 100%, 100%`,
    },
    [theme.breakpoints.down(500)]: {
      backgroundSize: `170% 100%, 100%`,
    },
    [theme.breakpoints.down(400)]: {
      backgroundSize: `225% 100%, 100%`,
    },
  },
}));
export const StyledMacWindow = styled('div', {
  shouldForwardProp: (prop) => prop !== 'ref',
})(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  width: '75%',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  paddingTop: '42px',
  borderRadius: '24px',
  backdropFilter: 'blur(64px)',
  [theme.breakpoints.down('xl')]: {
    width: '75%',
  },

  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

export const BorderStack = styled(Stack)(() => ({
  width: '100%',
  background: 'rgba(255, 255, 255, 0.2)',
  height: '1px',
  marginTop: '-1px',
}));

export const MenuCircles = styled('div')(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  top: '15px',
  left: '20px',
  ['div']: {
    width: '12px',
    height: '12px',
    border: '1px solid #777B7C',
    borderRadius: '50%',
    marginRight: theme.spacing(1),
  },
}));

export const ContentWrapper = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(10),
  padding: theme.spacing(10, 0),
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(10, 2),
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 4),
    gap: theme.spacing(6),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 1),
    gap: theme.spacing(4),
  },
}));

export const HeadingWrapper = styled(Stack)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}));

export const StyledHeroHeading = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 0),

  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  background:
    'linear-gradient(180deg, #FFFFFF 0%, rgba(217, 217, 217, 0.8) 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

export const StyledHeaderLogo = styled(Typography)(({ theme }) => ({
  ['&:after']: {
    content: "''",
    display: 'block',
    backgroundImage: `url(${rumbleStamp})`,
    backgroundSize: '100%',
    backgroundRepeat: 'no-repeat',
    height: '125px',
    width: '125px',
    position: 'absolute',
    top: '-42%',
    right: '-22%',

    [theme.breakpoints.down('lg')]: {
      height: '95px',
      width: '95px',
    },
    [theme.breakpoints.down('md')]: {
      height: '75px',
      width: '75px',
    },
    [theme.breakpoints.down('sm')]: {
      height: '60px',
      width: '60px',
      right: '-15%',
    },
  },
}));

export const StyledUnderHeading = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.PRIMARY,
  b: {
    whiteSpace: 'nowrap',
    position: 'relative',
    ['&:after']: {
      content: "''",
      display: 'block',
      backgroundImage: `url(${underlineStroke})`,
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      height: '100%',
      width: '100%',
      position: 'absolute',
      left: '0%',
    },
  },
}));

export const StyledHeroDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.SECONDARY,
}));

export const StyledScheduleCallArea = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
  },
}));

export const StyledButtonWrapper = styled(Stack)({
  display: 'flex',
  alignItems: 'center',
});

export const StyledLink = styled(Link)(({ theme }) => ({
  margin: theme.spacing(2, 0, 1, 1),
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  '.MuiStack-root': {
    backgroundColor: 'transparent !important',
    border: '1px solid white',
    boxShadow: `0px 0px 6px 4px ${theme.palette.orangeColors?.PLUS_2}, 0px 0px 6px 4px ${theme.palette.orangeColors?.PLUS_2} inset !important`,
  },
}));

export const StyledKeepScrolling = styled(Stack)(({ theme }) => ({
  height: theme.spacing(16),
  width: theme.spacing(16),
  backgroundImage: `url(${keepScrolling})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '95%',
  position: 'absolute',
  right: '10%',
  bottom: '8%',
  [theme.breakpoints.down('md')]: {
    position: 'static',
    marginLeft: theme.spacing(5.5),
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(4.5),
  },
}));
